import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiFetch } from "@/lib/api";
import { Calendar, ChevronRight } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  thumbnailUrl: string;
  published: boolean;
  createdAt: string;
  publishedAt?: string;
}

// Strip HTML tags for plain-text excerpt preview
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/&[^;]+;/g, ' ').replace(/\s+/g, ' ').trim();
}

/* @section: blog */
export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<BlogPost | null>(null);

  useEffect(() => {
    apiFetch("/blog")
      .then((r) => r.json())
      .then((d) => {
        const raw: BlogPost[] = d.data ?? [];
        raw.sort((a, b) => {
          const da = new Date((a.publishedAt && a.publishedAt.trim()) ? a.publishedAt : a.createdAt).getTime();
          const db = new Date((b.publishedAt && b.publishedAt.trim()) ? b.publishedAt : b.createdAt).getTime();
          return db - da;
        });
        setPosts(raw); setLoading(false);
      })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#2E8B57] font-sans">
      <Navbar />

      {/* Header */}
      <div className="pt-16 bg-[#2E8B57] pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center py-16">
          <div className="w-12 h-1 bg-[#FFE000] mx-auto mb-4" />
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">養生博客</h1>
          <p className="text-gray-400 text-lg">最新養生知識・保健心得・健康生活分享</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="bg-gray-100 rounded-2xl h-64 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-6xl mb-4">📝</p>
            <h2 className="text-2xl font-bold text-white mb-3">即將推出</h2>
            <p className="text-gray-200">敬請期待我們的養生知識分享！</p>
          </div>
        ) : selected ? (
          /* Post detail view — white card for readability */
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8">
            <button onClick={() => setSelected(null)}
              className="flex items-center gap-2 text-[#2E8B57] font-medium mb-6 hover:text-[#1C6034] transition-colors">
              ← 返回博客列表
            </button>
            {selected.thumbnailUrl && (
              <img src={selected.thumbnailUrl} alt={selected.title}
                   className="w-full h-64 object-cover rounded-2xl mb-6 shadow-md" />
            )}
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
              <Calendar size={14} />
              <span>{new Date(((selected as any).publishedAt?.trim()) ? (selected as any).publishedAt : selected.createdAt).toLocaleDateString("zh-HK")}</span>
            </div>
            <h1 className="text-3xl font-bold text-[#0A1628] mb-6">{selected.title}</h1>
            <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: selected.content }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <div key={post.id}
                onClick={() => setSelected(post)}
                className="group bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                {post.thumbnailUrl ? (
                  <img src={post.thumbnailUrl} alt={post.title}
                       className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-44 bg-gradient-to-br from-[#1C6034] to-[#2E8B57] flex items-center justify-center">
                    <span className="text-4xl">📰</span>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                    <Calendar size={12} />
                    <span>{new Date((post.publishedAt && post.publishedAt.trim()) ? post.publishedAt : post.createdAt).toLocaleDateString("zh-HK")}</span>
                  </div>
                  <h3 className="text-[#0A1628] font-bold text-lg mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-gray-500 text-sm line-clamp-3 mb-4">
                    {post.excerpt ? stripHtml(post.excerpt) : stripHtml(post.content).substring(0, 120) + "..."}
                  </p>
                  <span className="flex items-center gap-1 text-[#FFE000] font-semibold text-sm group-hover:gap-2 transition-all">
                    閱讀更多 <ChevronRight size={14} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
